// Lightweight keys + private realtime hint handling. Imported by the root
// realtime manager, so it must not pull the conversation UI or transport.
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth-store';
import { sessionRevision, sessionScope, sessionTenantId } from '@/features/auth/session-scope';

const ROOT = 'plugins-inbox';
const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
const uuid = (v: unknown): v is string => typeof v === 'string' && UUID.test(v) && v !== '00000000-0000-0000-0000-000000000000';

/** Owner-scoped keys: [root, kind, scope, revision, ...resource]. Never locale/JWT. */
export const pluginInboxKeys = {
  contributions: (scope: string, revision: number, conversationId: string, messageId: string | null, slot: string | null) => [ROOT, 'contributions', scope, revision, conversationId, messageId, slot] as const,
  hints: (scope: string, revision: number, conversationId: string) => [ROOT, 'hints', scope, revision, conversationId] as const,
  operation: (scope: string, revision: number, operationId: string) => [ROOT, 'operation', scope, revision, operationId] as const,
  panel: (scope: string, revision: number, conversationId: string, installationId: string, panelId: string) => [ROOT, 'panel', scope, revision, conversationId, installationId, panelId] as const,
  identity: (scope: string, revision: number, installationId: string, conversationId: string) => [ROOT, 'identity', scope, revision, installationId, conversationId] as const,
  /** Immutable per digest: the digest changes with the pinned version and with the grant. */
  presentation: (scope: string, revision: number, installationId: string, versionId: string, digest: string) => [ROOT, 'presentation', scope, revision, installationId, versionId, digest] as const,
};

function ownedKey(key: QueryKey, scope: string, revision: number, kinds: string[]): boolean {
  return key[0] === ROOT && kinds.includes(String(key[1])) && key[2] === scope && key[3] === revision;
}

export interface PluginHint { operationId: string }
/** `plugins:changed` arrives as the native directed envelope
 * {type, tenant_id, user_id, data:{version:1, tenant_id, kind:"operation", ids:{operation_id}}}.
 * Every declared owner must be this exact session; unknown versions/kinds are ignored. */
export function ownedPluginHint(message: { type?: unknown; tenant_id?: unknown; user_id?: unknown; data?: unknown }): PluginHint | null {
  const state = useAuthStore.getState(), tenant = sessionTenantId(state), user = state.user?.id;
  if (message.type !== 'plugins:changed' || !uuid(tenant) || !uuid(user)) return null;
  if (message.tenant_id !== tenant || message.user_id !== user) return null;
  const data = message.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  if (d.version !== 1 || d.tenant_id !== tenant || d.kind !== 'operation' || Object.keys(d).some(k => !['version', 'tenant_id', 'kind', 'ids'].includes(k))) return null;
  const ids = d.ids;
  if (!ids || typeof ids !== 'object' || Array.isArray(ids)) return null;
  const keys = Object.keys(ids);
  const operationId = (ids as Record<string, unknown>).operation_id;
  if (keys.length !== 1 || keys[0] !== 'operation_id' || !uuid(operationId)) return null;
  return { operationId };
}

/** The payload carries no customer data: re-read the operation and the
 * owner's contribution/hint/panel reads (degraded state depends on outcomes).
 * Restart pre-hint reads; skip when the session changed meanwhile. */
export async function reconcilePluginHint(client: QueryClient, hint: PluginHint): Promise<void> {
  const revision = sessionRevision(), scope = sessionScope(useAuthStore.getState());
  if (scope === 'anonymous') return;
  const operation = (q: { queryKey: QueryKey }) => ownedKey(q.queryKey, scope, revision, ['operation']) && q.queryKey[4] === hint.operationId;
  const reads = (q: { queryKey: QueryKey }) => ownedKey(q.queryKey, scope, revision, ['contributions', 'hints']);
  await client.cancelQueries({ predicate: operation });
  if (revision !== sessionRevision()) return;
  await Promise.all([
    client.invalidateQueries({ predicate: operation }, { cancelRefetch: false }),
    client.invalidateQueries({ predicate: reads }, { cancelRefetch: false }),
  ]);
}
